import { useState } from "react";
import { AnalysisCampaignObject } from "../../../../types/APITypes";
import { Flex, FlexBox, Heading, Icon, Label, LinkButton, Modal, ModalBody, ModalHeading, Tag } from "@braze/beacon-core";
import { ProgressMetric } from "../../shared/ProgressMetric";
import styled from "styled-components";
import { getColor, useColor } from "@braze/beacon-styling";
import { ModalPieChart } from "./ModalPieChart";
import { NumberTotals } from "./NumberTotals";

export const CampaignModal = (({campaign} : {campaign:AnalysisCampaignObject}) => {
    const [isOpen, setIsOpen] = useState(false);
    const colorGetter = useColor();
    const today = new Date();
    const actual_metrics = [
        {"title" : "NNLs", "value" : campaign.nnl_count, "color" : colorGetter("lightBlue", 400)},
        {"title" : "MQLs", "value" : campaign.mql_count, "color" : colorGetter("lightGreen", 400)},
        {"title" : "SQLs", "value" : campaign.sql_count, "color" : colorGetter("red", 400)},
        {"title" : "Pipe Count", "value" : campaign.pipe_count, "color" : colorGetter("orange", 400)},
        {"title" : "Pipe Amount ($)", "value" : campaign.pipe_amount, "color" : colorGetter("lightBlue", 500)},
    ]
    function costPerMetric(value : number) {
        return value === 0 || campaign.budget_amount === null ? "N/A" : campaign.budget_amount/value;
    }
    const cost_per_metrics = [
        {"title" : "Cost per NNL ($)", "value" : costPerMetric(campaign.nnl_count), "color" : colorGetter("lightBlue", 400)},
        {"title" : "Cost per MQLs ($)", "value" : costPerMetric(campaign.mql_count), "color" : colorGetter("lightGreen", 400)},
        {"title" : "Cost per SQLs ($)", "value" : costPerMetric(campaign.sql_count), "color" : colorGetter("red", 400)},
        {"title" : "Cost per Pipe Count ($)", "value" : costPerMetric(campaign.pipe_count), "color" : colorGetter("orange", 400)},
        {"title" : "Pipeline to Budget Ratio ($)", "value" : Math.round((campaign.pipe_amount/campaign.budget_amount + Number.EPSILON)*100)/100, "color" : colorGetter("lightBlue", 500)},
    ]

    return (
        <Flex style={{borderBottom : "0px"}}> 
            <Label onClick={() => setIsOpen(!isOpen)} style={{borderBottom:"0px"}}>{campaign.campaign_name_final}</Label>
            <Modal
                isOpen={isOpen}
                toggle={() => setIsOpen(!isOpen)}
                size="xl"
            >
                <ModalHeading>
                    <Flex alignItems="center">
                        <Heading level={2}>{campaign.campaign_name_final}</Heading>
                        
                        <LinkButton 
                            href={`https://braze.lightning.force.com/lightning/r/Campaign/${campaign.campaign_id}/view`}
                            size="sm" 
                            style={{marginLeft: "2rem", background: "white", borderWidth: "0px"}}>
                            <Icon name="arrow-up-right-from-square" style={{color: colorGetter("lightBlue", 500)}}/>
                        </LinkButton>
                    </Flex>
                    
                </ModalHeading>

                <ModalBody style={{width: "100%"}}>
                    <TagContainer>  
                        <StyledTag className="fiscal">{"FY" + campaign.fiscal_year}</StyledTag>
                        <StyledTag className="fiscal">{"Q" + campaign.fiscal_quarter}</StyledTag>
                        <StyledTag className="region">{campaign.campaign_region}</StyledTag>
                        <StyledTag className="region">{campaign.campaign_subregion}</StyledTag>
                    </TagContainer>
                    <Flex direction="column" style={{padding: "2rem"}}>
                        <Heading level={3} style={{marginBottom: "2rem"}}>Campaign Details</Heading>
                        <Flex direction="column" style={{marginBottom: "1rem"}}>
                            <DetailValue title="Campaign Owner:" content={campaign.campaign_owner}/>
                            <DetailValue title="Campaign Description:" content={campaign.campaign_description}/>
                            <DetailValue title="Campaign Type:" content={campaign.campaign_type}/>
                            <Flex>
                                <Flex direction="column" style={{width: "100%"}}>
                                    <DetailValue title="Campaign Objective:" content={campaign.campaign_objective}/>
                                    <DetailValue title="Start Date:" content={campaign.start_date !== null ? campaign.start_date.slice(5, 16) : ""}/>
                                    <DetailValue title="Budget Source:" content={campaign.budget_source}/>
                                </Flex>
                                <Flex direction="column" style={{width: "100%"}}>
                                    <DetailValue title="Campaign Source:" content={campaign.campaign_source}/>
                                    <DetailValue title="Days Elapsed:" content={campaign.start_date !== null ? Math.round((today.getTime() - new Date(campaign.start_date).getTime())/(1000 * 3600 * 24)).toString() : "N/A"}/>
                                    <DetailValue title="Budget Amount ($):" content={campaign.budget_amount}/>
                                </Flex>
                                
                            </Flex>
                        </Flex>
                        <Heading level={3} style={{marginBottom: "0rem"}}>Funnel & Avg. Cost Metrics</Heading>
                        <Flex style={{width: "100%", height: "250px", marginBottom:"1rem"}}>
                            <NumberTotals values={actual_metrics} />
                            <ModalPieChart campaign={campaign}/>
                            <NumberTotals values={cost_per_metrics} />
                        </Flex>
                        
                        <Heading level={3} style={{marginBottom: "2rem"}}>Actual vs. Projected Campaign Metrics</Heading>
                        
                            
                            {campaign.nnl_projected_count !== null ? 
                                <Flex direction="column">
                                    <ProgressMetric actual={campaign.nnl_count} estimated={campaign.nnl_projected_count} title="NNLs"/>
                                    <ProgressMetric actual={campaign.mql_count} estimated={campaign.mql_projected_count} title="MQLs"/>
                                    <ProgressMetric actual={campaign.sql_count} estimated={campaign.sql_projected_count} title="SQLs"/>
                                    <ProgressMetric actual={campaign.pipe_count} estimated={campaign.pipe_projected_count} title="Pipe #"/>
                                </Flex> 
                                :
                                <Label>No Projected Values Recorded</Label>
                            }
                            
                            
                       
                        
                    </Flex>
                </ModalBody>
            </Modal>
        </Flex>
    )
})

const DetailValue = (({title, content} : {title : string, content : string | number}) => {

    return (
        <DetailContainer>
            <Label className="title">{title}</Label>
            <Label className="content">{content}</Label>
        </DetailContainer>
    )
})

const DetailContainer = styled(Flex)`
    .title {
        font-size: 14px;
        margin-bottom: 0px;
    }
    .content {
        font-size: 14px;
        margin-left: 1rem;
        font-weight: normal;
        margin-bottom: 0px;
        margin-left: 1rem;
    }
    // max-width: 80%;
    margin-bottom: 1.5rem;
    
`
const StyledTag = styled(Tag)`
    justify-content: center;
    width: 100%;
    font-size: 14px;
    .status {
        // width: 4rem;
        background: yellow;
    }
`
const TagContainer = styled(FlexBox)`
    flex-direction: row;
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 2rem;
    
    .fiscal {
        background: ${getColor("lightBlue", 500)};
    }
    .region {
        background: ${getColor("red", 500)}
    }

    > * {
        &:not(:last-of-type) {
        margin-right: 1rem;
        }
    }
    
`

